
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from '@/components/ui/alert-dialog';
import {
  Clipboard,
  MoreHorizontal,
  Trash2,
  TrendingUp
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { CampaignLink } from '@/types/referral';

interface CampaignLinksTableProps {
  campaignLinks: CampaignLink[];
  loading: boolean;
  error: string | null;
  deleteCampaignLink: (id: string) => Promise<boolean>;
}

export function CampaignLinksTable({
  campaignLinks,
  loading,
  error,
  deleteCampaignLink
}: CampaignLinksTableProps) {
  const { toast } = useToast();

  const handleCopyLink = async (url: string, campaignName: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link copied",
        description: `Campaign link for "${campaignName}" copied to clipboard.`
      });
    } catch (err) {
      toast({
        title: "Copy failed",
        description: "Failed to copy link to clipboard.",
        variant: "destructive"
      });
    }
  };

  const handleDeleteCampaign = async (id: string) => {
    await deleteCampaignLink(id);
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Campaign Links</CardTitle>
          <CardDescription>Manage your tracked campaign links</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Campaign Links</CardTitle>
          <CardDescription>Manage your tracked campaign links</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <TrendingUp className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              Create your first campaign link to track specific referral campaigns and see them here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (campaignLinks.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Campaign Links</CardTitle>
          <CardDescription>Manage your tracked campaign links</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-4">
            <TrendingUp className="h-8 w-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground text-sm">
              Create your first campaign link to track specific referral campaigns and see them here
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Campaign Links</CardTitle>
        <CardDescription>
          Track visits to your referral link from different sources (Instagram, Facebook, etc.)
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Campaign</TableHead>
                <TableHead className="text-center">Signups</TableHead>
                <TableHead className="text-center">Conversions</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaignLinks.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium">{campaign.campaign_name}</div>
                      <div className="text-sm text-muted-foreground">
                        {campaign.campaign_slug}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">0</Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant="secondary">0</Badge>
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleCopyLink(campaign.referral_url, campaign.campaign_name)}
                        >
                          <Clipboard className="h-4 w-4 mr-2" />
                          Copy Campaign Link
                        </DropdownMenuItem>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <DropdownMenuItem
                              onSelect={(e) => e.preventDefault()}
                              className="text-red-600 focus:text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Campaign Link</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the campaign "{campaign.campaign_name}"? 
                                This action cannot be undone and all tracking data will be lost.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleDeleteCampaign(campaign.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
